import React, { useContext } from "react";
import { ModalContext } from "context/ModalContext";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Button } from "react-bootstrap";
import { show_toast } from "utils/helpers";
import { format, parse, isValid, isAfter } from "date-fns";

export default function DownloadSettlementForm({ underwriterId }) {
  const { modalClose } = useContext(ModalContext);

  // Validation schema using Yup and date-fns
  const validationSchema = Yup.object().shape({
    fileType: Yup.string().required("File type is required"),
    filter: Yup.string().required("Filter is required"),
    fromDate: Yup.date()
      .transform((value, originalValue) => {
        const parsedDate = parse(originalValue, "dd/MM/yyyy", new Date());
        return isValid(parsedDate) ? parsedDate : null;
      })
      .required("From date is required")
      .nullable()
      .test("valid-date", "Invalid date", (value) => isValid(value))
      .test(
        "from-before-to",
        "From date must be before or equal to To date",
        function (value) {
          const { toDate } = this.parent;
          if (!value || !toDate) return true;
          return !isAfter(value, toDate);
        }
      ),
    toDate: Yup.date()
      .transform((value, originalValue) => {
        const parsedDate = parse(originalValue, "dd/MM/yyyy", new Date());
        return isValid(parsedDate) ? parsedDate : null;
      })
      .required("To date is required")
      .nullable()
      .test("valid-date", "Invalid date", (value) => isValid(value)),
  });

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const formattedValues = {
        fileType: values.fileType,
        filter: values.filter,
        fromDate: format(values.fromDate, "dd/MM/yyyy"),
        toDate: format(values.toDate, "dd/MM/yyyy"),
        underwriterId,
      };
      console.log("Submitting form with values:", formattedValues);
      // Replace with actual API call
      // await (await api_srv).download_settlement(formattedValues);

      show_toast("Settlement file download initiated", "success");
      modalClose();
    } catch (err) {
      console.error("Error initiating download:", err);
      show_toast(
        `Failed to initiate download: ${
          err.message || "An unexpected error occurred"
        }`,
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Get today's date for initial values
  const today = new Date();
  const initialFromDate = format(
    new Date(today.getFullYear(), today.getMonth(), 1),
    "dd/MM/yyyy"
  );
  const initialToDate = format(today, "dd/MM/yyyy");

  return (
    <div className="p-4 bg-white rounded">
      <Formik
        initialValues={{
          fileType: "PDF",
          filter: "All",
          fromDate: initialFromDate,
          toDate: initialToDate,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched, isSubmitting }) => (
          <Form className="mb-3">
            <div className="row row-cols-1 g-3">
              {/* File Type Radio Buttons */}
              <div className="col">
                <label className="form-label">File Type</label>
                <div className="d-flex" style={{ gap: "2rem" }}>
                  <div className="form-check">
                    <input
                      type="radio"
                      name="fileType"
                      value="PDF"
                      checked={values.fileType === "PDF"}
                      onChange={() => setFieldValue("fileType", "PDF")}
                      className="form-check-input"
                      id="pdfRadio"
                    />
                    <label className="form-check-label" htmlFor="pdfRadio">
                      PDF
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      name="fileType"
                      value="Excel"
                      checked={values.fileType === "Excel"}
                      onChange={() => setFieldValue("fileType", "Excel")}
                      className="form-check-input"
                      id="excelRadio"
                    />
                    <label className="form-check-label" htmlFor="excelRadio">
                      Excel
                    </label>
                  </div>
                </div>
                <ErrorMessage
                  name="fileType"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </div>

              {/* Filter Radio Buttons */}
              <div className="col">
                <label className="form-label">Filter</label>
                <div className="d-flex" style={{ gap: "2rem" }}>
                  <div className="form-check">
                    <input
                      type="radio"
                      name="filter"
                      value="All"
                      checked={values.filter === "All"}
                      onChange={() => setFieldValue("filter", "All")}
                      className="form-check-input"
                      id="allRadio"
                    />
                    <label className="form-check-label" htmlFor="allRadio">
                      All
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      name="filter"
                      value="Paid"
                      checked={values.filter === "Paid"}
                      onChange={() => setFieldValue("filter", "Paid")}
                      className="form-check-input"
                      id="paidRadio"
                    />
                    <label className="form-check-label" htmlFor="paidRadio">
                      Paid
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      name="filter"
                      value="Unpaid"
                      checked={values.filter === "Unpaid"}
                      onChange={() => setFieldValue("filter", "Unpaid")}
                      className="form-check-input"
                      id="unpaidRadio"
                    />
                    <label className="form-check-label" htmlFor="unpaidRadio">
                      Unpaid
                    </label>
                  </div>
                </div>
                <ErrorMessage
                  name="filter"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </div>

              {/* From Date Input */}
              <div className="col">
                <label htmlFor="fromDate" className="form-label">
                  From Date (dd/MM/yyyy)
                </label>
                <input
                  name="fromDate"
                  type="text"
                  value={values.fromDate}
                  onChange={(e) => setFieldValue("fromDate", e.target.value)}
                  className={`form-control ${
                    errors.fromDate && touched.fromDate ? "is-invalid" : ""
                  }`}
                  placeholder="dd/MM/yyyy"
                />
                <ErrorMessage
                  name="fromDate"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              {/* To Date Input */}
              <div className="col">
                <label htmlFor="toDate" className="form-label">
                  To Date (dd/MM/yyyy)
                </label>
                <input
                  name="toDate"
                  type="text"
                  value={values.toDate}
                  onChange={(e) => setFieldValue("toDate", e.target.value)}
                  className={`form-control ${
                    errors.toDate && touched.toDate ? "is-invalid" : ""
                  }`}
                  placeholder="dd/MM/yyyy"
                />
                <ErrorMessage
                  name="toDate"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              {/* Preview Section */}
              <div className="mt-4">
                <p className="text-muted">→ Click download</p>
                <div
                  className="border rounded p-3"
                  style={{ backgroundColor: "#f8f9fa" }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>Type:</strong> {values.fileType}<br />
                      <strong>Filter:</strong> {values.filter}<br />
                      <strong>From:</strong> {values.fromDate}<br />
                      <strong>To:</strong> {values.toDate}<br />
                      <strong>Underwriter ID:</strong> {underwriterId || "N/A"}
                    </div>
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={isSubmitting}
                      className="d-flex align-items-center"
                    >
                      {isSubmitting ? (
                        <>
                          <svg
                            className="spinner-border spinner-border-sm me-2"
                            viewBox="0 0 24 24"
                            role="status"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              className="opacity-25"
                            />
                            <path
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              className="opacity-75"
                            />
                          </svg>
                          Please wait...
                        </>
                      ) : (
                        "Download"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}